import {
  type Extension,
  StateField,
  StateEffect,
  RangeSet,
} from "@codemirror/state";
import {
  EditorView,
  Decoration,
  type DecorationSet,
  gutter,
  GutterMarker,
} from "@codemirror/view";
import type { Comment } from "../../types/document";
import { useCollaborationStore } from "../../stores/collaboration";
import { useUIStore } from "../../stores/ui";

interface CommentGutterData {
  comments: Comment[];
  focusedCommentId: string | null;
}

const setCommentData = StateEffect.define<CommentGutterData>();

class CommentMarker extends GutterMarker {
  commentId: string;
  count: number;
  isFocused: boolean;

  constructor(commentId: string, count: number, isFocused: boolean) {
    super();
    this.commentId = commentId;
    this.count = count;
    this.isFocused = isFocused;
  }

  toDOM() {
    const el = document.createElement("div");
    el.title = `${this.count} comment${this.count > 1 ? "s" : ""}`;
    el.style.cssText = `
      width: 6px; height: 6px; border-radius: 50%; cursor: pointer;
      margin: 5px 4px;
      background: var(--color-cool-azure);
      opacity: ${this.isFocused ? "1" : "0.55"};
      transform: scale(${this.isFocused ? "1.4" : "1"});
      transition: opacity 100ms, transform 100ms;
    `;

    el.addEventListener("mouseenter", () => {
      el.style.opacity = "1";
      el.style.transform = "scale(1.4)";
    });
    el.addEventListener("mouseleave", () => {
      if (!this.isFocused) {
        el.style.opacity = "0.55";
        el.style.transform = "scale(1)";
      }
    });

    const capturedId = this.commentId;
    el.addEventListener("click", (e) => {
      e.stopPropagation();
      useCollaborationStore.getState().setFocusedComment(capturedId);
      useUIStore.getState().openSidebarTab("comments");
    });

    return el;
  }
}

const commentLineBg = Decoration.line({ class: "lever-comment-line" });
const commentLineFocusedBg = Decoration.line({ class: "lever-comment-line-focused" });

interface CommentGutterState {
  markers: Map<number, CommentMarker>;
  decorations: DecorationSet;
}

function computeState(
  data: CommentGutterData,
  doc: { lines: number; line: (n: number) => { from: number } },
): CommentGutterState {
  const { comments, focusedCommentId } = data;
  const rootComments = comments.filter((c) => !c.parent_id);
  const markers = new Map<number, CommentMarker>();
  const decos: { from: number; deco: Decoration }[] = [];

  const lineCommentMap = new Map<number, { ids: string[]; count: number }>();
  for (const c of rootComments) {
    if (c.resolved) continue;
    for (let line = c.line_start; line <= c.line_end; line++) {
      if (line < 1 || line > doc.lines) continue;
      const entry = lineCommentMap.get(line) ?? { ids: [], count: 0 };
      entry.ids.push(c.id);
      entry.count++;
      lineCommentMap.set(line, entry);
    }
  }

  const focusedComment = focusedCommentId
    ? rootComments.find((c) => c.id === focusedCommentId)
    : null;

  for (const [lineNum, entry] of lineCommentMap) {
    const isFocusedLine =
      focusedComment &&
      lineNum >= focusedComment.line_start &&
      lineNum <= focusedComment.line_end;

    if (!markers.has(lineNum)) {
      markers.set(
        lineNum,
        new CommentMarker(entry.ids[0], entry.count, !!isFocusedLine),
      );
    }

    const lineFrom = doc.line(lineNum).from;
    decos.push({
      from: lineFrom,
      deco: isFocusedLine ? commentLineFocusedBg : commentLineBg,
    });
  }

  decos.sort((a, b) => a.from - b.from);

  return {
    markers,
    decorations: Decoration.set(decos.map((d) => d.deco.range(d.from))),
  };
}

const commentField = StateField.define<CommentGutterState>({
  create() {
    return { markers: new Map(), decorations: RangeSet.empty };
  },
  update(value, tr) {
    for (const effect of tr.effects) {
      if (effect.is(setCommentData)) {
        return computeState(effect.value, tr.state.doc);
      }
    }
    if (tr.docChanged) {
      return {
        markers: value.markers,
        decorations: value.decorations.map(tr.changes),
      };
    }
    return value;
  },
  provide: (field) => EditorView.decorations.from(field, (v) => v.decorations),
});

const commentGutter = gutter({
  class: "lever-comment-gutter",
  markers: (view) => {
    const state = view.state.field(commentField, false);
    if (!state) return RangeSet.empty;

    const ranges: { from: number; marker: CommentMarker }[] = [];
    for (const [lineNum, marker] of state.markers) {
      if (lineNum >= 1 && lineNum <= view.state.doc.lines) {
        const line = view.state.doc.line(lineNum);
        ranges.push({ from: line.from, marker });
      }
    }
    ranges.sort((a, b) => a.from - b.from);
    return RangeSet.of(ranges.map(({ from, marker }) => marker.range(from)));
  },
});

export function commentGutterExtension(): Extension {
  return [commentField, commentGutter];
}

export function updateCommentGutter(
  view: EditorView,
  comments: Comment[],
  focusedCommentId: string | null,
) {
  view.dispatch({ effects: setCommentData.of({ comments, focusedCommentId }) });
}
